import React, { useCallback, useEffect, useMemo, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import jaLocale from "@fullcalendar/core/locales/ja";
import MypageLayout from "./MypageLayout";
import { emitToast } from "./Toast";
import { ymd } from "./dateUtil";
import ErrorBar from "./ErrorBar";
import Loader from "./Loader";
import apiErrorMessage from "./apiError";
import { jsonHeaders } from "./csrf";

// シフト・有休設定ページ（管理者用・セッション認証）。Porcelain テーマ。
// 構成：
//   1. 全社員の休みカレンダー（俯瞰・表示専用。承認済み=確定 / 申請中 を色分け）
//   2. 社員を選択 → 週次パターン7曜日（編集）
//   3. 有休設定（社員×年度の付与日数を設定＋消化・残日数を表示）
// ※ 日ごとの調整は「日付例外」を廃止し、社員の休み申請 → 経営者が承認リストで承認、で行う。
const API_URL = process.env.REACT_APP_API_URL || "";

const MODE_OPTIONS = [
  { value: "office", text: "事務所" },
  { value: "remote", text: "在宅" },
];
const TYPE_LABEL = { paid: "有給", special: "特別休暇", absence: "欠勤" };

// FullCalendar の allDay イベントは end が排他的なので、date_to の翌日を渡す。
function addDayStr(dateStr) {
  const d = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  d.setDate(d.getDate() + 1);
  return ymd(d);
}
// 日本の年度（4月始まり）。
function fiscalYearOf(d) { const m = d.getMonth() + 1; return m >= 4 ? d.getFullYear() : d.getFullYear() - 1; }
// 休みカレンダーの初期表示月＝当月の1日（ハードコードを廃止し当月に追従させる）。
const THIS_MONTH_START = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-01`; })();
// "YYYY-MM-DD" → "M/D"。モバイルの休み一覧で使う。
function mdLabel(dateStr) {
  const s = String(dateStr).slice(0, 10);
  const [, m, d] = s.split("-");
  return m && d ? `${parseInt(m, 10)}/${parseInt(d, 10)}` : s;
}
// 期間ラベル：単日は "M/D"、複数日は "M/D〜M/D"。
function mdRange(from, to) {
  const a = mdLabel(from);
  const b = mdLabel(to);
  return a === b ? a : `${a}〜${b}`;
}

function ShiftManagementPage() {
  const [employees, setEmployees] = useState([]);
  // 社員タブ："all"（全社員）＋各社員ID。既定は全社員俯瞰。
  const [tab, setTab] = useState("all");
  const [weekdaysJa, setWeekdaysJa] = useState(["月", "火", "水", "木", "金", "土", "日"]);
  const [patterns, setPatterns] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");

  // 全社員の休み（カレンダー俯瞰用）
  const [leaves, setLeaves] = useState([]);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 860 : false));

  // 有休設定
  const [grants, setGrants] = useState([]);          // [{fiscal_year, granted_days, used_days, remaining_days}]
  const [grantYear, setGrantYear] = useState(null);  // 選択中の年度
  const [grantDays, setGrantDays] = useState("");     // 入力中の付与日数
  const [savingGrant, setSavingGrant] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 860);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // タブから派生：選択中の社員ID（"all" は空＝設定は非表示）と対象社員。
  // 経営者は勤怠を登録しない＝シフトも有休も持たないので設定対象から外す。
  // 出すと経営者にシフトを設定でき、その瞬間から打刻忘れ通知の判定対象になってしまう
  // （通知側でも経営者は除外しているが、そもそも設定させない）。
  // ※ /api/employee/list/ は給与明細のアップロード画面(PayslipAdminPage)とも共有のため
  //   API側では絞らない。あちらは経営者も対象に残す必要がある。
  const selectedId = tab === "all" ? "" : tab;
  const activeEmployees = employees.filter((e) => e.is_active && !e.is_executive);
  const selectedEmp = employees.find((e) => String(e.id) === tab) || null;

  const fetchEmployees = useCallback(async () => {
    setLoadingList(true); setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/list/`, { credentials: "include" });
      if (res.status === 401) { setError("ログインが必要です。"); return; }
      if (res.status === 403) { setError("管理者権限が必要です。"); return; }
      if (!res.ok) throw new Error("取得に失敗しました");
      const data = await res.json();
      setEmployees(data.results || []);
    } catch (e) {
      setError("社員一覧の取得に失敗しました。");
    } finally {
      setLoadingList(false);
    }
  }, []);

  // 全社員の休み申請（承認済み＋申請中）をカレンダー用に取得。
  const fetchLeaves = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/employee/leave/list/`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      setLeaves(data.results || []);
    } catch (e) { /* カレンダーは補助表示なので握りつぶす */ }
  }, []);

  const fetchShiftDetail = useCallback(async (employeeId) => {
    setLoadingDetail(true); setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/shifts/?employee_id=${encodeURIComponent(employeeId)}`, { credentials: "include" });
      if (!res.ok) throw new Error("取得に失敗しました");
      const data = await res.json();
      if (Array.isArray(data.weekdays_ja) && data.weekdays_ja.length === 7) setWeekdaysJa(data.weekdays_ja);
      setPatterns((data.patterns || []).map((p) => ({
        ...p,
        start_time: p.start_time || "",
        end_time: p.end_time || "",
        default_mode: p.default_mode || "office",
        is_dayoff: Boolean(p.is_dayoff),
      })));
    } catch (e) {
      setError("シフトの取得に失敗しました。");
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // 有休サマリ（社員×年度）を取得。年度の初期選択は最新（先頭）。
  const fetchPaidLeave = useCallback(async (employeeId) => {
    try {
      const res = await fetch(`${API_URL}/api/employee/paid-leave/?employee_id=${encodeURIComponent(employeeId)}`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json();
      const rows = data.results || [];
      setGrants(rows);
      const first = rows[0];
      setGrantYear(first ? first.fiscal_year : fiscalYearOf(new Date()));
      setGrantDays(first ? String(first.granted_days) : "");
    } catch (e) { /* 有休は補助表示 */ }
  }, []);

  useEffect(() => { fetchEmployees(); fetchLeaves(); }, [fetchEmployees, fetchLeaves]);
  useEffect(() => {
    if (selectedId) { fetchShiftDetail(selectedId); fetchPaidLeave(selectedId); }
  }, [selectedId, fetchShiftDetail, fetchPaidLeave]);

  // 年度を切り替えたら、その年度の付与日数を入力欄に反映。
  const onChangeGrantYear = (fy) => {
    setGrantYear(fy);
    const row = grants.find((g) => g.fiscal_year === fy);
    setGrantDays(row ? String(row.granted_days) : "");
  };

  const updatePatternRow = (weekday, field, value) => {
    setPatterns((prev) => prev.map((p) => (p.weekday === weekday ? { ...p, [field]: value } : p)));
  };

  const savePatternRow = async (row) => {
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/shifts/pattern/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({
          employee_id: Number(selectedId), weekday: row.weekday,
          start_time: row.is_dayoff ? "" : row.start_time,
          end_time: row.is_dayoff ? "" : row.end_time,
          default_mode: row.default_mode, is_dayoff: row.is_dayoff,
        }),
      });
      if (!res.ok) { setError(await apiErrorMessage(res, `${weekdaysJa[row.weekday]}曜日の保存に失敗しました。`)); return; }
      const saved = await res.json();
      setPatterns((prev) => prev.map((p) => (p.weekday === row.weekday ? {
        ...p, id: saved.id,
        start_time: saved.start_time || "", end_time: saved.end_time || "",
        default_mode: saved.default_mode || "office", is_dayoff: Boolean(saved.is_dayoff),
      } : p)));
      emitToast(`${weekdaysJa[row.weekday]}曜日を保存しました。`);
    } catch (e) {
      setError(`${weekdaysJa[row.weekday]}曜日の保存に失敗しました。`);
    }
  };

  const saveGrant = async () => {
    if (grantYear == null) return;
    setSavingGrant(true); setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/paid-leave/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({
          employee_id: Number(selectedId), fiscal_year: grantYear,
          granted_days: Number(grantDays) || 0,
        }),
      });
      if (!res.ok) { setError(await apiErrorMessage(res, "有休付与の保存に失敗しました。")); return; }
      await fetchPaidLeave(selectedId);
      setGrantYear(grantYear); // fetchPaidLeave が最新年度に戻すため選択を維持
      emitToast(`${grantYear}年度の有休付与を保存しました。`);
    } catch (e) {
      setError("有休付与の保存に失敗しました。");
    } finally {
      setSavingGrant(false);
    }
  };

  // 休み申請 → カレンダーイベント（却下は除外、承認/申請中で色分け）。
  // 「すべて」なら全社員、社員タブ選択時はその社員の休みだけに絞る。
  const calendarEvents = useMemo(() => (leaves || [])
    .filter((l) => l.status === "approved" || l.status === "pending")
    .filter((l) => tab === "all" || l.employee_id === (selectedEmp ? selectedEmp.id : null))
    .map((l) => ({
      id: String(l.id),
      title: `${l.employee_name}・${TYPE_LABEL[l.type] || "休み"}`,
      start: String(l.date_from).slice(0, 10),
      end: addDayStr(l.date_to),
      allDay: true,
      classNames: [l.status === "approved" ? "mp-lvev-approved" : "mp-lvev-pending"],
    })), [leaves, tab, selectedEmp]);

  // モバイル用：カレンダーは色バーのみで誰が休みか分からないため、休み一覧を併記する。
  // カレンダーと同じ絞り込み（承認済み・申請中／タブ）で、日付順に並べる。
  const mobileLeaves = useMemo(() => (leaves || [])
    .filter((l) => l.status === "approved" || l.status === "pending")
    .filter((l) => tab === "all" || l.employee_id === (selectedEmp ? selectedEmp.id : null))
    .slice()
    .sort((a, b) => String(a.date_from).localeCompare(String(b.date_from))),
    [leaves, tab, selectedEmp]);

  const selectedGrant = grants.find((g) => g.fiscal_year === grantYear) || null;
  // 年度の選択肢（サマリの年度＋当年度）。
  const yearOptions = useMemo(() => {
    const set = new Set(grants.map((g) => g.fiscal_year));
    set.add(fiscalYearOf(new Date()));
    return [...set].sort((a, b) => b - a);
  }, [grants]);

  return (
    <MypageLayout active="shift" title="シフト・有休">
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      {/* 社員タブ：すべて＝全社員俯瞰／各社員＝その社員のカレンダー＋設定 */}
      <div className="mp-tabs mp-emptabs">
        <button type="button" className={`mp-tab${tab === "all" ? " mp-on" : ""}`} onClick={() => setTab("all")}>すべて</button>
        {activeEmployees.map((emp) => (
          <button type="button" key={emp.id} disabled={loadingList} className={`mp-tab${tab === String(emp.id) ? " mp-on" : ""}`} onClick={() => setTab(String(emp.id))}>{emp.name}</button>
        ))}
      </div>

      {/* 休みカレンダー（俯瞰・表示専用）。すべて＝全社員／社員選択時＝その社員のみ。 */}
      <div className="mp-cal mp-cal-readonly" style={{ marginBottom: 12 }}>
        <FullCalendar
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          initialDate={THIS_MONTH_START}
          locale={jaLocale}
          height="auto"
          fixedWeekCount={false}
          headerToolbar={{ left: "prev,next today", center: "title", right: "" }}
          dayMaxEvents={isMobile ? 3 : 3}
          dayCellContent={(arg) => arg.dayNumberText.replace("日", "")}
          events={calendarEvents}
        />
      </div>
      <div className="mp-lv-legend">
        <span className="mp-lv-lg mp-lv-lg-approved">確定休み</span>
        <span className="mp-lv-lg mp-lv-lg-pending">申請中</span>
      </div>

      {/* モバイル：カレンダーが色バーのみのため、誰が休みかを一覧で併記する。 */}
      <div className="mp-lv-mob mp-only-mobile">
        {mobileLeaves.length === 0 ? (
          <div className="mp-lv-mob-empty">表示できる休みはありません</div>
        ) : mobileLeaves.map((l) => (
          <div className={`mp-lv-mrow${l.status === "approved" ? " mp-lv-mrow-on" : " mp-lv-mrow-pend"}`} key={l.id}>
            <span className="mp-lv-mdate">{mdRange(l.date_from, l.date_to)}</span>
            <span className="mp-lv-mname">{l.employee_name}</span>
            <span className="mp-lv-mtype">{TYPE_LABEL[l.type] || "休み"}</span>
            <span className="mp-lv-mst">{l.status === "approved" ? "確定" : "申請中"}</span>
          </div>
        ))}
      </div>

      {/* 選択社員の 週次パターン・有休設定（すべて選択時は非表示） */}
      {tab === "all" ? null : loadingDetail ? (
        <Loader />
      ) : (
        <>
          {/* 有休設定（上）→ 週次パターン（下）の順で表示 */}
          <h2 className="mp-h2">有休設定</h2>
          <div className="mp-pl-editrow">
            <div className="mp-pl-field">
              <label className="mp-flabel">年度</label>
              <select className="mp-select" value={grantYear ?? ""} onChange={(e) => onChangeGrantYear(Number(e.target.value))}>
                {yearOptions.map((fy) => <option key={fy} value={fy}>{fy}年度</option>)}
              </select>
            </div>
            <div className="mp-pl-field">
              <label className="mp-flabel">付与日数</label>
              <div className="mp-pl-daysinput">
                <input className="mp-input" type="number" min="0" step="1" value={grantDays}
                  onChange={(e) => setGrantDays(e.target.value)} />
                <span className="mp-pl-unit">日</span>
              </div>
            </div>
            <div className="mp-pl-field mp-pl-savefield">
              <button className="mp-btn mp-primary mp-sm" disabled={savingGrant} onClick={saveGrant}>
                {savingGrant ? "保存中…" : "保存"}
              </button>
            </div>
            {/* 行の右端：使用 / 有休総数（消化 / 付与） */}
            <div className="mp-pl-usage">
              <span className="mp-pl-usage-cap">使用 / 有休総数</span>
              <span className="mp-pl-usage-val">{selectedGrant ? selectedGrant.used_days : 0} / {selectedGrant ? selectedGrant.granted_days : (Number(grantDays) || 0)}</span>
            </div>
          </div>

          {/* 年度別の一覧 */}
          {grants.length > 0 && (
            <div className="mp-tablewrap" style={{ marginTop: 18, marginBottom: 30 }}>
              <table className="mp-table mp-pl-table">
                <thead>
                  <tr><th>年度</th><th>付与</th><th>消化</th><th>残</th></tr>
                </thead>
                <tbody>
                  {grants.map((g) => (
                    <tr key={g.fiscal_year}>
                      <td className="mp-nowrap">{g.fiscal_year}年度</td>
                      <td>{g.granted_days}日</td>
                      <td>{g.used_days}日</td>
                      <td className="mp-pl-remain">{g.remaining_days}日</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2 className="mp-h2">週次パターン</h2>
          {/* デスクトップ：テーブル。保存ボタンは廃止し、変更時に自動保存（モバイルと挙動統一・#9）。 */}
          <div className="mp-tablewrap mp-only-desktop" style={{ marginBottom: 28 }}>
            <table className="mp-table">
              <thead>
                <tr><th>曜日</th><th>休み</th><th>始業</th><th>終業</th><th>勤務形態</th></tr>
              </thead>
              <tbody>
                {patterns.map((row) => (
                  <tr key={row.weekday}>
                    <td>{weekdaysJa[row.weekday]}</td>
                    <td>
                      <input type="checkbox" className="mp-check" checked={row.is_dayoff}
                        onChange={(e) => { const off = e.target.checked; updatePatternRow(row.weekday, "is_dayoff", off); savePatternRow({ ...row, is_dayoff: off }); }} />
                    </td>
                    <td>
                      <input className="mp-input" type="time" value={row.start_time} disabled={row.is_dayoff}
                        onChange={(e) => { const v = e.target.value; updatePatternRow(row.weekday, "start_time", v); if (v) savePatternRow({ ...row, start_time: v }); }} />
                    </td>
                    <td>
                      <input className="mp-input" type="time" value={row.end_time} disabled={row.is_dayoff}
                        onChange={(e) => { const v = e.target.value; updatePatternRow(row.weekday, "end_time", v); if (v) savePatternRow({ ...row, end_time: v }); }} />
                    </td>
                    <td>
                      <select className="mp-select" value={row.default_mode} disabled={row.is_dayoff}
                        onChange={(e) => { const mode = e.target.value; updatePatternRow(row.weekday, "default_mode", mode); savePatternRow({ ...row, default_mode: mode }); }}>
                        {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* モバイル：曜日ごとに1行（案A）。白パネル内を区切り線のリストに。
              曜日・始業–終業・形態・トグルを横一列。保存ボタンは廃止し変更時に自動保存。 */}
          <div className="mp-swk-list mp-only-mobile">
            {patterns.map((row) => (
              <div className={`mp-swk${row.is_dayoff ? " mp-swk-off" : ""}`} key={row.weekday}>
                <span className="mp-swk-day">{weekdaysJa[row.weekday]}</span>
                {row.is_dayoff ? (
                  <span className="mp-swk-restday">終日休み</span>
                ) : (
                  <div className="mp-swk-fields">
                    <input className="mp-swk-time" type="time" value={row.start_time}
                      onChange={(e) => { const v = e.target.value; updatePatternRow(row.weekday, "start_time", v); if (v) savePatternRow({ ...row, start_time: v }); }} />
                    <span className="mp-swk-dash">–</span>
                    <input className="mp-swk-time" type="time" value={row.end_time}
                      onChange={(e) => { const v = e.target.value; updatePatternRow(row.weekday, "end_time", v); if (v) savePatternRow({ ...row, end_time: v }); }} />
                    <select className="mp-swk-mode" value={row.default_mode}
                      onChange={(e) => { const mode = e.target.value; updatePatternRow(row.weekday, "default_mode", mode); savePatternRow({ ...row, default_mode: mode }); }}>
                      {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
                    </select>
                  </div>
                )}
                <label className="mp-swk-toggle">
                  <input type="checkbox" className="mp-swk-cb" checked={row.is_dayoff}
                    onChange={(e) => { const off = e.target.checked; updatePatternRow(row.weekday, "is_dayoff", off); savePatternRow({ ...row, is_dayoff: off }); }} />
                  <span className="mp-swk-track" aria-hidden="true" />
                </label>
              </div>
            ))}
          </div>
        </>
      )}
    </MypageLayout>
  );
}

export default ShiftManagementPage;
